/*
 * Jira Ticket:
 * Created Date: Fri, 21st Feb 2020, 13:37:19 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React from 'react';
import { useTranslate } from 'react-admin';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import { connect } from 'react-redux';
import {
  Button,
  CardActions,
  CircularProgress,
  withStyles,
} from '@material-ui/core';
import {
  PasswordField,
  renderFormFieldInput as renderInput,
} from '@thedistance/the-core-cms-module-authentication-amplify';
import { formStyles as styles, pageStyles } from 'resources/auth/styles';
import { validatePassword, validateEmail } from 'resources/auth/util';
import logo from '../../../assets/PASMAlogo2.png';

const RegistrationForm = withStyles(styles)(props => {
  const { requestRegistration } = props;
  const translate = useTranslate();

  const pageStyle = pageStyles();

  return (
    <>
      <div style={pageStyle.avatar}>
        <img alt="logo" src={logo} style={pageStyle.icon} />
      </div>

      <Form
        validate={values => {
          const errors = {
            ...validatePassword(values, { translate }),
            ...validateEmail(values, { translate }),
          };
          if (!values.givenName)
            errors.givenName = translate('ra.validation.required');
          if (!values.familyName)
            errors.familyName = translate('ra.validation.required');
          if (!values.position)
            errors.position = translate('ra.validation.required');
          return errors;
        }}
        onSubmit={values => {
          requestRegistration(values);
        }}
        {...props}
      >
        {({ classes, isLoading, handleSubmit, submitting }) => (
          <form onSubmit={e => handleSubmit(e)}>
            <div className={classes.form}>
              <div className={classes.input}>
                <Field
                  id="givenName"
                  name="givenName"
                  label={translate('authentication.register.givenName')}
                  render={renderInput}
                  disabled={isLoading || submitting}
                />
              </div>
              <div className={classes.input}>
                <Field
                  id="familyName"
                  name="familyName"
                  label={translate('authentication.register.familyName')}
                  render={renderInput}
                  disabled={isLoading || submitting}
                />
              </div>
              <div className={classes.input}>
                <Field
                  id="position"
                  name="position"
                  label={translate('authentication.register.position')}
                  render={renderInput}
                  disabled={isLoading || submitting}
                />
              </div>
              <div className={classes.input}>
                <Field
                  id="email"
                  name="email"
                  label={translate('authentication.register.email')}
                  render={renderInput}
                  disabled={isLoading || submitting}
                />
              </div>
              <div className={classes.input}>
                <PasswordField
                  isFormLoading={isLoading || submitting}
                  label={translate('authentication.register.password')}
                  fieldComponent={renderInput}
                />
              </div>
              <div className={classes.input}>
                <Field
                  id="certificateNumber"
                  name="certificateNumber"
                  label={translate('authentication.register.certificate')}
                  render={renderInput}
                  disabled={isLoading || submitting}
                />
              </div>
            </div>
            <CardActions>
              <Button
                type="submit"
                disabled={isLoading}
                className={classes.button}
              >
                {isLoading ? (
                  <CircularProgress
                    size={25}
                    thickness={3}
                    className={classes.loadingIndicator}
                  />
                ) : (
                  translate('authentication.register.submit')
                )}
              </Button>
            </CardActions>
          </form>
        )}
      </Form>
    </>
  );
});

RegistrationForm.propTypes = {
  classes: PropTypes.object,
  requestRegistration: PropTypes.func.isRequired,
};

RegistrationForm.defaultProps = {
  classes: null,
};

const mapStateToProps = state => ({ isLoading: state.admin.loading > 0 });

export default connect(mapStateToProps)(RegistrationForm);
